import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Btn, Empty, Badge } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { FLabel, FInput } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { Th, Td } from "./ChartPanels";
import { Plus, Pencil, Trash2, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { ClientFormsPanel } from "../components/ClientFormsPanel";

const IconBtn = ({ testId, label, onClick, danger, children }) => (
  <button type="button" data-testid={testId} aria-label={label} title={label} onClick={onClick}
    className={`h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center ${danger ? "hover:bg-[#FFEBEE] hover:text-[#C62828]" : "hover:bg-[#EDE7DC]"}`}>{children}</button>
);

// ---------------- Pharmacy ----------------
const PH_EMPTY = { name: "", phone: "", fax: "", address: "", city: "", state: "", zip: "", preferred: false, notes: "" };
export function PharmacyPanel({ client, editable }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(PH_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [dirOpen, setDirOpen] = useState(false);
  const [dir, setDir] = useState([]);
  const [dirQ, setDirQ] = useState("");
  const load = useCallback(() => api.get(`/clients/${client.id}/pharmacies`).then((r) => setRows(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!dirOpen) return;
    const t = setTimeout(() => {
      api.get(`/pharmacy-directory${dirQ ? `?q=${encodeURIComponent(dirQ)}` : ""}`)
        .then((r) => setDir(r.data)).catch(() => setDir([]));
    }, 200);
    return () => clearTimeout(t);
  }, [dirOpen, dirQ]);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/pharmacies/${editId}`, editLinked ? { preferred: form.preferred, notes: form.notes } : form);
      else await api.post(`/clients/${client.id}/pharmacies`, { ...form, client_notes: form.notes, notes: "" });
      toast.success(editId ? "Pharmacy saved" : "Pharmacy added here and to the Pharmacy Directory"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const addFromDirectory = async (p) => {
    try {
      await api.post(`/clients/${client.id}/pharmacies`, { directory_id: p.id, preferred: rows.length === 0 });
      toast.success(`${p.name} added to this client`); setDirOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const linkToDirectory = async (p) => {
    try {
      const d = await api.post("/pharmacy-directory", { name: p.name, phone: p.phone, fax: p.fax, address: p.address, city: p.city, state: p.state, zip: p.zip });
      await api.put(`/pharmacies/${p.id}`, { directory_id: d.data.id });
      toast.success(`${p.name} is now linked to the Pharmacy Directory`); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const editLinked = !!rows.find((r) => r.id === editId)?.directory_id;
  const doDelete = async () => { await api.delete(`/pharmacies/${del.id}`); toast.success("Pharmacy removed"); setDel(null); load(); };
  return (
    <>
      <ClientFormsPanel client={client} category="Orders" match="medication|prescri|pharmac|med " title="Medication Orders" emptyHide />
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm text-[#7B6157]">Pharmacies used by this client. Each one comes from the clinic Pharmacy Directory, so contact changes show everywhere. The preferred one fills in new prescriptions.</p>
        <div className="flex flex-wrap items-center gap-2">
          {editable && <Btn data-testid="pharmacy-from-directory-btn" variant="outline" className="rounded-lg" onClick={() => { setDirQ(""); setDirOpen(true); }}><Plus size={14} /> From Directory</Btn>}
          {editable && <Btn data-testid="add-pharmacy-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(PH_EMPTY); setEditId(null); setPushToDir(false); setOpen(true); }}><Plus size={14} /> Add Pharmacy</Btn>}
        </div>
      </div>
      {rows.length === 0 ? <Empty text="No pharmacies on file" /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((p) => (
            <div key={p.id} data-testid={`pharmacy-card-${p.id}`} className={`rounded-2xl border p-4 ${p.preferred ? "border-[#FFB300] bg-[#FFFDF5]" : "border-[#EFEBE0]"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1.5 font-heading text-sm font-bold text-[#3E2723]">{p.name}{p.preferred && <span className="inline-flex items-center gap-0.5 rounded-full bg-[#FFF3D6] px-2 py-0.5 text-[10px] font-bold text-[#8D5A00]"><Star size={9} /> PREFERRED</span>}</div>
                  <div className="mt-1 text-xs text-[#6D4C41]">{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ") || "No address on file"}</div>
                  <div className="mt-1 text-xs text-[#7B6157] tabular-nums">{p.phone && `Ph: ${p.phone}`} {p.fax && ` · Fax: ${p.fax}`}</div>
                  {p.notes && <div className="mt-1 text-[11px] text-[#84675B]">{p.notes}</div>}
                  {p.in_directory ? <div className="mt-1.5 text-[10px] font-semibold text-[#2E7D32]" data-testid={`pharmacy-linked-${p.id}`}>From the Pharmacy Directory{p.directory_archived ? ", archived there" : ""}</div>
                    : <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-semibold text-[#8D5A00]" data-testid={`pharmacy-unlinked-${p.id}`}>Typed by hand, not in the Pharmacy Directory
                      {editable && <button type="button" data-testid={`pharmacy-link-${p.id}`} onClick={() => linkToDirectory(p)} className="rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[#8D5A00] hover:bg-[#FFE0B2]">Add to directory</button>}</div>}
                </div>
                <div className="flex gap-1.5">
                  {editable && <IconBtn testId={`edit-pharmacy-${p.id}`} label="Edit" onClick={() => { setForm({ ...PH_EMPTY, ...Object.fromEntries(Object.keys(PH_EMPTY).map((k) => [k, p[k] ?? ""])) }); setEditId(p.id); setOpen(true); }}><Pencil size={13} /></IconBtn>}
                  {editable && <IconBtn testId={`delete-pharmacy-${p.id}`} label="Delete" danger onClick={() => setDel(p)}><Trash2 size={13} /></IconBtn>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <SlidePanel open={open} onClose={() => setOpen(false)} title={editId ? "Edit Pharmacy" : "Add Pharmacy"} subtitle={`${client.first_name} ${client.last_name}`} testId="pharmacy-slide-panel">
        <form onSubmit={save} className="space-y-4">
          {editLinked ? <p className="rounded-xl bg-[#FDFCF8] px-3 py-2 text-xs text-[#6D4C41]" data-testid="pharmacy-linked-hint"><strong>{form.name}</strong>. Contact details come from the Pharmacy Directory. Change them in Settings, Pharmacy Directory, and they update for every client.</p> : <>
          <div><FLabel>Pharmacy name</FLabel><FInput data-testid="pharmacy-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Phone</FLabel><FInput data-testid="pharmacy-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><FLabel>Fax</FLabel><FInput value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} /></div>
          </div>
          <div><FLabel>Street address</FLabel><FInput value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><FLabel>City</FLabel><FInput value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><FLabel>State</FLabel><FInput value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><FLabel>Zip</FLabel><FInput value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
          </div>
          {!editId && <p className="text-[11px] text-[#7B6157]">This pharmacy is also added to the clinic Pharmacy Directory, so it can be picked on prescriptions.</p>}
          </>}
          <label className="flex items-center gap-2 text-sm font-semibold text-[#3E2723] cursor-pointer">
            <input data-testid="pharmacy-preferred" type="checkbox" checked={!!form.preferred} onChange={(e) => setForm({ ...form, preferred: e.target.checked })} className="h-4 w-4 accent-[#4E342E]" />
            Preferred pharmacy (fills in new prescriptions and refill requests)
          </label>
          <div><FLabel>Notes for this client</FLabel><FInput data-testid="pharmacy-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4"><Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="pharmacy-save-btn" type="submit">{editId ? "Save" : "Add"}</Btn></div>
        </form>
      </SlidePanel>
      <SlidePanel open={dirOpen} onClose={() => setDirOpen(false)} title="Add from Pharmacy Directory" subtitle={`${client.first_name} ${client.last_name}`} testId="pharmacy-directory-panel">
        <div className="space-y-3">
          <FInput data-testid="pharmacy-directory-search" placeholder="Search by name, city, or phone" value={dirQ} onChange={(e) => setDirQ(e.target.value)} />
          {dir.length === 0 ? (
            <Empty text="No matching directory entries. Use Add Pharmacy to add a new one to this client and the directory." />
          ) : (
            <div className="space-y-2">
              {dir.map((p) => (
                <button key={p.id} type="button" data-testid={`directory-pharmacy-${p.id}`} onClick={() => addFromDirectory(p)}
                  className="w-full text-left rounded-xl border border-[#EFEBE0] bg-white p-3 hover:bg-[#FDFCF8] transition-colors">
                  <div className="font-semibold text-sm text-[#3E2723]">{p.name}</div>
                  <div className="text-[11px] text-[#7B6157]">{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ") || "No address on file"}</div>
                  <div className="text-[11px] text-[#7B6157] tabular-nums">{p.phone ? `Ph: ${p.phone}` : ""}{p.fax ? ` · Fax: ${p.fax}` : ""}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </SlidePanel>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} title="Remove pharmacy" description={del ? `Remove ${del.name} from this client's pharmacies?` : ""} />
    </>
  );
}

// ---------------- Immunizations ----------------
const IMM_EMPTY = { vaccine: "", date: dayjs().format("YYYY-MM-DD"), dose: "", route: "", site: "", lot_number: "", manufacturer: "", administered_by: "", notes: "" };
export function ImmunizationsPanel({ client, editable }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(IMM_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get(`/clients/${client.id}/immunizations`).then((r) => setRows(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/immunizations/${editId}`, form);
      else await api.post(`/clients/${client.id}/immunizations`, form);
      toast.success("Immunization saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const doDelete = async () => { await api.delete(`/immunizations/${del.id}`); toast.success("Immunization deleted"); setDel(null); load(); };
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#7B6157]">Immunization history documented for this client.</p>
        {editable && <Btn data-testid="add-immunization-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(IMM_EMPTY); setEditId(null); setOpen(true); }}><Plus size={14} /> Add Immunization</Btn>}
      </div>
      {rows.length === 0 ? <Empty text="No immunizations recorded" /> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Vaccine</Th><Th>Date</Th><Th>Dose</Th><Th>Route / Site</Th><Th>Lot #</Th><Th>Administered By</Th><Th>{""}</Th></tr></thead>
            <tbody>{rows.map((m) => (
              <tr key={m.id} data-testid={`immunization-row-${m.id}`} className="hover:bg-[#FDFCF8]">
                <Td className="font-semibold text-[#3E2723]">{m.vaccine}{m.manufacturer ? <span className="block text-[11px] font-normal text-[#84675B]">{m.manufacturer}</span> : null}</Td>
                <Td className="tabular-nums">{dayjs(m.date).format("MM/DD/YYYY")}</Td>
                <Td>{m.dose || "-"}</Td>
                <Td>{[m.route, m.site].filter(Boolean).join(" / ") || "-"}</Td>
                <Td className="tabular-nums">{m.lot_number || "-"}</Td>
                <Td>{m.administered_by || "-"}</Td>
                <Td><div className="flex gap-2 justify-end">
                  {editable && <IconBtn testId={`edit-immunization-${m.id}`} label="Edit" onClick={() => { setForm({ ...IMM_EMPTY, ...Object.fromEntries(Object.keys(IMM_EMPTY).map((k) => [k, m[k] ?? ""])) }); setEditId(m.id); setOpen(true); }}><Pencil size={13} /></IconBtn>}
                  {editable && <IconBtn testId={`delete-immunization-${m.id}`} label="Delete" danger onClick={() => setDel(m)}><Trash2 size={13} /></IconBtn>}
                </div></Td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <SlidePanel open={open} onClose={() => setOpen(false)} title={editId ? "Edit Immunization" : "Add Immunization"} subtitle={`${client.first_name} ${client.last_name}`} testId="immunization-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div><FLabel>Vaccine</FLabel><FInput data-testid="immunization-vaccine" value={form.vaccine} onChange={(e) => setForm({ ...form, vaccine: e.target.value })} required placeholder="e.g. Influenza, Tdap, COVID-19" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Date administered</FLabel><DateField testId="immunization-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} noFuture /></div>
            <div><FLabel>Dose</FLabel><FInput data-testid="immunization-dose" value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })} placeholder="e.g. 0.5 mL, Dose 1 of 2" /></div>
            <div><FLabel>Route</FLabel><FInput value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} placeholder="IM, SubQ, Intranasal" /></div>
            <div><FLabel>Site</FLabel><FInput value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="Left deltoid" /></div>
            <div><FLabel>Lot number</FLabel><FInput value={form.lot_number} onChange={(e) => setForm({ ...form, lot_number: e.target.value })} /></div>
            <div><FLabel>Manufacturer</FLabel><FInput value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
          </div>
          <div><FLabel>Administered by</FLabel><FInput value={form.administered_by} onChange={(e) => setForm({ ...form, administered_by: e.target.value })} /></div>
          <div><FLabel>Notes</FLabel><FInput value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4"><Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="immunization-save-btn" type="submit">{editId ? "Save" : "Add"}</Btn></div>
        </form>
      </SlidePanel>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} title="Delete immunization" description={del ? `Delete ${del.vaccine} dated ${dayjs(del.date).format("MM/DD/YYYY")}?` : ""} />
    </>
  );
}

// ---------------- COM (Client Outcome Measures) ----------------
export function ComPanel({ client }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/outcome-measures", { params: { client_id: client.id } }).then((r) => setRows(r.data)).catch(() => {}); }, [client.id]);
  const riskTone = (r) => (r === "high" ? "bg-[#FFEBEE] text-[#C62828]" : r === "moderate" ? "bg-[#FFF3E0] text-[#B84A00]" : "bg-[#E8F5E9] text-[#2E7D32]");
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#7B6157]">Client Outcome Measures (COM): standardized instruments administered to this client.</p>
        <Btn data-testid="new-outcome-btn" variant="outline" className="rounded-lg" onClick={() => navigate("/outcome-measures")}><Plus size={14} /> New Administration</Btn>
      </div>
      {rows.length === 0 ? <Empty text="No outcome measures administered yet" /> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Instrument</Th><Th>Date</Th><Th>Score</Th><Th>Severity</Th><Th>Risk</Th><Th>Administered By</Th></tr></thead>
            <tbody>{rows.map((o) => (
              <tr key={o.id} data-testid={`com-row-${o.id}`} className="hover:bg-[#FDFCF8]">
                <Td className="font-semibold text-[#3E2723]">{o.instrument_name || o.instrument}</Td>
                <Td className="tabular-nums">{dayjs(o.date).format("MM/DD/YYYY")}</Td>
                <Td className="tabular-nums font-bold">{o.score}{o.max_score ? ` / ${o.max_score}` : ""}</Td>
                <Td>{o.severity || "-"}</Td>
                <Td>{o.risk_level ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${riskTone(o.risk_level)}`}>{o.risk_level}</span> : "-"}</Td>
                <Td>{o.administered_by || "-"}</Td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[10px] text-[#84675B]">Scores are screening aids, not diagnoses. Clinical judgment and a full assessment remain required (APA / instrument publisher guidance).</p>
    </>
  );
}
